// The customer picker's model: a directory of customers (id + name) matched by what the operator
// types, ranked name-prefix first, then name contains, then id. Pure;
// tests/unit/customers.test.ts. Where the names come from is ui/customer-directory.tsx.

export interface CustomerEntry {
  id: string;
  name: string;
}

function normalise(text: string): string {
  return text.trim().toLowerCase();
}

/** Entries matching `query`, best first; every entry (by name) when the query is blank. */
export function matchCustomers(entries: readonly CustomerEntry[], query: string, limit = 50): CustomerEntry[] {
  const q = normalise(query);
  const byName = (a: CustomerEntry, b: CustomerEntry) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
  if (q === "") return [...entries].sort(byName).slice(0, limit);
  const ranked: { entry: CustomerEntry; rank: number }[] = [];
  for (const entry of entries) {
    const name = normalise(entry.name);
    const id = normalise(entry.id);
    let rank = -1;
    if (name.startsWith(q)) rank = 0;
    else if (name.split(/\s+/).some((word) => word.startsWith(q))) rank = 1;
    else if (name.includes(q)) rank = 2;
    else if (id.startsWith(q)) rank = 3;
    else if (id.includes(q)) rank = 4;
    if (rank >= 0) ranked.push({ entry, rank });
  }
  return ranked
    .sort((a, b) => a.rank - b.rank || byName(a.entry, b.entry))
    .slice(0, limit)
    .map((r) => r.entry);
}

/** A customer's display name, or its id when the directory does not know it. */
export function customerName(entries: readonly CustomerEntry[], id: string): string {
  return entries.find((entry) => entry.id === id)?.name ?? id;
}

/** Merge entries from several sources: the first name seen for an id wins, then sorted by name. */
export function mergeDirectory(...sources: readonly (readonly CustomerEntry[])[]): CustomerEntry[] {
  const byId = new Map<string, CustomerEntry>();
  for (const source of sources) {
    for (const entry of source) {
      if (entry.id === "") continue;
      const known = byId.get(entry.id);
      if (!known || (known.name === known.id && entry.name !== entry.id)) byId.set(entry.id, entry);
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}

/** Whether the typed text is a plausible customer id to use as-is (the picker's fallback). */
export function looksLikeCustomerId(text: string): boolean {
  return /^[a-z0-9-]{1,64}$/.test(text.trim());
}
